import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  RawHttpResponse,
  TokenInfoOptions,
  TokenInfoResponse,
  TokenOptions,
  UserInfoResponse,
  jwks as universalJwks,
  token as universalToken,
  tokenInfo as universalTokenInfo,
  userInfo as universalUserInfo,
} from './identity-oauth-v1-refresh-token-oauth.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/identity' };

export function token(httpClient: HttpClient): TokenSignature {
  return (options?: TokenOptions) =>
    universalToken(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface TokenSignature {
  /**
   * Creates an access token.
   *
   *
   * The endpoint accepts raw HTTP requests. You must pass the request's body
   * parameters formatted as bytes in the raw HTTP request's `body` field,
   * following this template:
   * `{"grantType": "client_credentials", "client_id": "<APP_ID>", "client_secret": "<APP_SECRET_KEY>", "instance_id": "<INSTANCE_ID>"}`.
   *
   * When the call succeeds, Wix returns `{"statusCode": 200}` and the created access
   * token in the `body` field of the raw HTTP response.
   *
   * In case the call fails, Wix returns the relevant `4XX` error code in the raw
   * HTTP response's `statusCode` field and details
   * about the error in `body`. Error details follow the
   * [conventions of the Internet Engineering Task Force (IETF)](https://datatracker.ietf.org/doc/html/rfc6749#appendix-A.7).
   */
  (options?: TokenOptions): Promise<
    NonNullablePaths<
      RawHttpResponse,
      `body` | `headers` | `headers.${number}.key` | `headers.${number}.value`,
      2
    >
  >;
}

export function jwks(httpClient: HttpClient): JwksSignature {
  return () =>
    universalJwks(
      // @ts-ignore
      { httpClient }
    );
}

interface JwksSignature {
  /**
   * Returns the JWKS (JSON Web Key Set) with the RSA public key used to verify asymmetrically-signed
   * tokens (e.g. OIDC id_token). Required by external OIDC relying parties.
   * Serves a non-secret public key.
   */
  (): Promise<
    NonNullablePaths<
      RawHttpResponse,
      `body` | `headers` | `headers.${number}.key` | `headers.${number}.value`,
      2
    >
  >;
}

export function tokenInfo(httpClient: HttpClient): TokenInfoSignature {
  return (options?: TokenInfoOptions) =>
    universalTokenInfo(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface TokenInfoSignature {
  /**
   * Token Introspection Endpoint.
   */
  (options?: TokenInfoOptions): Promise<
    NonNullablePaths<
      TokenInfoResponse,
      `active` | `subjectType` | `subjectId` | `clientId`,
      0
    >
  >;
}

export function userInfo(httpClient: HttpClient): UserInfoSignature {
  return () =>
    universalUserInfo(
      // @ts-ignore
      { httpClient }
    );
}

interface UserInfoSignature {
  /**
   * OpenID Connect UserInfo endpoint
   * Should be called with user access token obtained in OIDC flow as Authorization header
   * @returns UserInfo endpoint response for OpenID Connect
   */
  (): Promise<NonNullablePaths<UserInfoResponse, `sub`, 0>>;
}

export {
  ActionEvent,
  AuthorizeRequest,
  DeviceAuthorizationInfoRequest,
  DeviceAuthorizationInfoResponse,
  DeviceAuthorizeRequest,
  DeviceAuthorizeResponse,
  DeviceCodeRequest,
  DeviceCodeResponse,
  DeviceVerifyRequest,
  DeviceVerifyResponse,
  DeviceVerifyV2Request,
  DeviceVerifyV2Response,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  HeadersEntry,
  InvalidateUserCodeRequest,
  InvalidateUserCodeResponse,
  JwksRequest,
  PathParametersEntry,
  QueryParametersEntry,
  RawHttpRequest,
  RawHttpResponse,
  RefreshToken,
  RestoreInfo,
  RevokeRefreshTokenRequest,
  RevokeRefreshTokenResponse,
  SubjectType,
  TokenInfoOptions,
  TokenInfoResponse,
  TokenOptions,
  UserBlockedEvent,
  UserDeletedEvent,
  UserInfoRequest,
  UserInfoResponse,
} from './identity-oauth-v1-refresh-token-oauth.universal.js';
